"use client";
import React from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  useMediaQuery,
  styled,
  IconButton,
  Tooltip,
} from "@mui/material";

import { useSelector } from "react-redux";
import { useSession } from "next-auth/react";
import { useRouter, usePathname } from "next/navigation";

import ThemeToggle from "./ThemeToggle";
import Profile from "./Profile";
import MobileSidebar from "@/app/components/dashboards/layout/sidebar/MobileSidebar";
import Logo from "@/app/components/Logo";
import AdminUserButton from "@/app/components/admin/AdminUserButton";
import AdminMeetingButton from "@/app/components/admin/AdminMeetingButton";
import AdminModerationButton from "@/app/components/admin/AdminModerationButton";
import AdminServiceButton from "@/app/components/admin/AdminServiceButton";
import AdminAnalyticsButton from "@/app/components/admin/AdminAnalyticsButton";
// import AdminNotificationButton from "@/app/components/admin/AdminNotificationButton";
import PersonSearchRounded from "@mui/icons-material/PersonSearchRounded";

const HeaderButton = styled(IconButton)(({ theme, selected }) => ({
  marginLeft: theme.spacing(1),
  transition: "all 0.3s ease",
  color: selected ? theme.palette.grey[600] : theme.palette.grey[500],
  backgroundColor: selected ? theme.palette.action.selected : "transparent",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.grey[600],
  },
}));

const Header = () => {
  const router = useRouter();
  const lgDown = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const customizer = useSelector((state) => state.customizer);
  const pathname = usePathname();

  // Get session and check if user has admin privileges
  const { data: session } = useSession();
  const hasAdminPrivileges = session?.user?.tier?.admin_tier;

  const navigateSafely = (href) => {
    const event = new CustomEvent("meetingNavigationRequest", {
      detail: { href },
      cancelable: true,
      bubbles: false,
    });
    const shouldNavigate = document.dispatchEvent(event);
    if (shouldNavigate) {
      router.push(href);
    }
  };

  const handleSearchClick = () => {
    navigateSafely("/search");
  };

  const isSearchPage = pathname === "/search";

  const AppBarStyled = styled(AppBar)(({ theme }) => ({
    background: "transparent",
    justifyContent: "center",
    position: "fixed",
    top: 0,
    width: "100%",
    height: `${customizer.TopbarHeight}px`,
    minHeight: `calc(${customizer.TopbarHeight}px + env(safe-area-inset-top))`,
    zIndex: theme.zIndex.drawer + 1,
    paddingTop: "env(safe-area-inset-top)",
    paddingLeft: "env(safe-area-inset-left)",
    paddingRight: "env(safe-area-inset-right)",
    boxShadow: "none",
    // PWA standalone mode specific
    "@media (display-mode: standalone)": {
      paddingTop: "env(safe-area-inset-top)",
      minHeight: `calc(${customizer.TopbarHeight}px + env(safe-area-inset-top))`,
    },
    // iOS Safari specific
    "@supports (-webkit-touch-callout: none)": {
      paddingTop: "env(safe-area-inset-top)",
      minHeight: `calc(${customizer.TopbarHeight}px + env(safe-area-inset-top))`,
    },
  }));

  const CapsuleContainer = styled(Box)(({ theme }) => ({
    margin: "0 auto",
    marginTop: "16px",
    marginLeft: "16px",
    marginRight: "16px",
    width: "calc(100% - 32px)",
    [theme.breakpoints.up("md")]: {
      marginLeft: "16px",
      marginRight: "16px",
      width: "calc(100% - 32px)",
    },
    borderRadius: "50px!important",
    background: `rgba(${
      theme.palette.mode === "dark" ? "26, 33, 41" : "255, 255, 255"
    }, ${theme.palette.mode === "dark" ? "0.8" : "0.85"})`,
    backdropFilter: "blur(20px) saturate(180%)",
    border: `1px solid rgba(${
      theme.palette.mode === "dark" ? "255, 255, 255" : "0, 0, 0"
    }, ${theme.palette.mode === "dark" ? "0.12" : "0.08"})`,
    boxShadow:
      theme.palette.mode === "dark"
        ? `0 4px 16px rgba(0, 0, 0, 0.2),
         0 1px 0px rgba(255, 255, 255, 0.03) inset`
        : `0 4px 16px rgba(0, 0, 0, 0.06),
         0 1px 0px rgba(255, 255, 255, 0.5) inset`,
  }));

  const ToolbarStyled = styled(Toolbar)(({ theme }) => ({
    width: "100%",
    minHeight: `${customizer.TopbarHeight - 16}px !important`,
    height: `${customizer.TopbarHeight - 16}px`,
    padding: "0 20px",
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      padding: "0 14px",
      minHeight: `${customizer.TopbarHeight - 20}px !important`,
      height: `${customizer.TopbarHeight - 20}px`,
    },
  }));

  const LogoContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    borderRadius: "25px!important",
    padding: "10px",
    margin: 0,
    [theme.breakpoints.up("md")]: {
      margin: "0 -14px",
    },
    transition: "background-color 0.2s ease",
    height: "auto",
    "&:hover": {
      backgroundColor: `rgba(${
        theme.palette.mode === "dark" ? "255, 255, 255" : "0, 0, 0"
      }, 0.05)`,
    },
  }));

  const ActionsContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.25),
    marginRight: "-20px",
    [theme.breakpoints.down("md")]: {
      marginRight: "-10px",
    },
  }));

  return (
    <>
      <AppBarStyled color="default">
        <CapsuleContainer>
          <ToolbarStyled>
            <LogoContainer>
              <Typography variant="h6" component="div" className="logo">
                <Logo width={26} height={26} />
              </Typography>
            </LogoContainer>

            <Box sx={{ flexGrow: 1 }} />

            <ActionsContainer>
              {/* Only show admin buttons on desktop and if user has admin privileges */}
              {!lgDown && hasAdminPrivileges && !session?.impersonating && (
                <>
                  <AdminUserButton />
                  <AdminMeetingButton />
                  <AdminModerationButton />
                  <AdminServiceButton />
                  <AdminAnalyticsButton />
                  {/* <AdminNotificationButton /> */}
                </>
              )}
              {lgDown && !isSearchPage && (
                <Tooltip title="Search Users" arrow placement="bottom">
                  <HeaderButton
                    onClick={handleSearchClick}
                    selected={isSearchPage}
                  >
                    <PersonSearchRounded sx={{ fontSize: 30 }} />
                  </HeaderButton>
                </Tooltip>
              )}
              <ThemeToggle />
              <Box sx={{ ml: { xs: 0, md: 1 } }} />
              {lgDown && <MobileSidebar />}
              <Profile />
            </ActionsContainer>
          </ToolbarStyled>
        </CapsuleContainer>
      </AppBarStyled>
    </>
  );
};

export default Header;
